import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectCrossClientResultsExecution } from '../../../../store/crossClient/crossClientResults'
import { triggerCrossClientActionsPolling } from '../../../../store/crossClient/crossClientActions'
import { useUpdateCrossClientIntegrationEntitiesMutation } from '../../../../store/crossClient/crossClient'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { addToast } from '../../../../store/toast/toastSlice'
import { type IntegrationServiceId } from '../../../../common/integrations'
import { TrackEvent, useTracking } from '../../../../apps/professional-app/fa/common/tracking'

import { type ActionTracking } from '../useActionTracking'
import { type CrmEligibility } from './useCrmEligibility'

interface ToastConfig {
  title: string
  message: string
}

interface UseCrmActionSaveOptions {
  eligibility: CrmEligibility
  onClose: () => void
  serviceId: IntegrationServiceId
  sourceObject: string
  entityType: string
  relatedToType: string
  sourcePlatform?: string
  successToast: ToastConfig
  errorToast: ToastConfig
  tracking: ActionTracking
}

interface UseCrmActionSaveResult {
  save: (entity: Record<string, unknown>) => Promise<void>
  saving: boolean
}

export const useCrmActionSave = ({
  eligibility,
  onClose,
  serviceId,
  sourceObject,
  entityType,
  relatedToType,
  sourcePlatform = 'zocks',
  successToast,
  errorToast,
  tracking,
}: UseCrmActionSaveOptions): UseCrmActionSaveResult => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const execution = useAppSelector(selectCrossClientResultsExecution)
  const { trackingClient } = useTracking()

  const [updateIntegrationEntities, { isLoading: saving }] = useUpdateCrossClientIntegrationEntitiesMutation()

  const save = async (entity: Record<string, unknown>) => {
    if (eligibility.status !== 'ready' || !execution?.id || !accountId) {
      return
    }

    trackingClient.track({ eventName: TrackEvent.X_CLIENT_ACTIONS_SUBMITTED, eventDetails: { ...tracking } })

    try {
      await updateIntegrationEntities({
        accountId,
        executionId: execution.id,
        connector: serviceId,
        relatedToType,
        sourcePlatform,
        sourceObject,
        entityType,
        entity,
        targets: eligibility.eligibleTargets.map(({ clientId, externalId }) => ({ clientId, externalId })),
      }).unwrap()

      dispatch(triggerCrossClientActionsPolling(Date.now()))
      dispatch(addToast({ variant: 'success', title: successToast.title, messages: [successToast.message] }))
      onClose()
    } catch {
      dispatch(addToast({ variant: 'error', title: errorToast.title, messages: [errorToast.message] }))
    }
  }

  return { save, saving }
}
